import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'Psy-Trainer';
const DEFAULT_KEYWORDS = 'Psy-Trainer, психологические тесты, самооценка состояния, ментальное здоровье';
const INDEX_META = 'index, follow';
const NOINDEX_META = 'noindex, nofollow';

export const ROUTE_META = {
  '/': {
    title: `Главная | ${SITE_NAME}`,
    description: 'Psy-Trainer помогает выбрать психологический тест, пройти самооценку состояния и отслеживать личную динамику после регистрации.',
    keywords: DEFAULT_KEYWORDS,
    ogDescription: 'Публичная витрина Psy-Trainer с каталогом психологических тестов и защищенным личным кабинетом.',
    robots: INDEX_META,
  },
  '/login': {
    title: `Вход | ${SITE_NAME}`,
    description: 'Войдите в Psy-Trainer, чтобы продолжить работу с тестами и результатами.',
    keywords: `${DEFAULT_KEYWORDS}, вход`,
    ogDescription: 'Страница входа в личный кабинет Psy-Trainer.',
    robots: INDEX_META,
  },
  '/register': {
    title: `Регистрация | ${SITE_NAME}`,
    description: 'Создайте аккаунт Psy-Trainer для прохождения тестов и сохранения личных результатов.',
    keywords: `${DEFAULT_KEYWORDS}, регистрация`,
    ogDescription: 'Регистрация в Psy-Trainer для безопасной работы с тестами.',
    robots: INDEX_META,
  },
  '/tests': {
    title: `Каталог тестов | ${SITE_NAME}`,
    description: 'Публичный каталог психологических тестов Psy-Trainer для самооценки состояния и выбора подходящего опросника.',
    keywords: `${DEFAULT_KEYWORDS}, психологические тесты`,
    ogDescription: 'Изучите доступные тесты Psy-Trainer и зарегистрируйтесь, чтобы пройти выбранный опросник.',
    robots: INDEX_META,
  },
  '/tests/:id': {
    title: `Прохождение теста | ${SITE_NAME}`,
    description: 'Закрытая страница прохождения теста Psy-Trainer без раскрытия персональных данных.',
    keywords: `${DEFAULT_KEYWORDS}, прохождение теста`,
    ogDescription: 'Проходите тест Psy-Trainer и сохраняйте результаты в личном кабинете.',
    robots: NOINDEX_META,
  },
  '/results': {
    title: `Результаты | ${SITE_NAME}`,
    description: 'Закрытый список результатов тестирования в Psy-Trainer без публикации персональных данных.',
    keywords: `${DEFAULT_KEYWORDS}, результаты тестов`,
    ogDescription: 'Просматривайте историю результатов Psy-Trainer в личном кабинете.',
    robots: NOINDEX_META,
  },
  '/results/:id': {
    title: `Детали результата | ${SITE_NAME}`,
    description: 'Закрытая страница просмотра результата тестирования в Psy-Trainer без раскрытия персональных данных.',
    keywords: `${DEFAULT_KEYWORDS}, результат теста`,
    ogDescription: 'Просмотр результата Psy-Trainer доступен только в защищенном личном кабинете.',
    robots: NOINDEX_META,
  },
  '/profile': {
    title: `Профиль | ${SITE_NAME}`,
    description: 'Закрытая страница профиля Psy-Trainer с настройками аккаунта без персональных данных в мета-тегах.',
    keywords: `${DEFAULT_KEYWORDS}, профиль пользователя`,
    ogDescription: 'Управляйте аккаунтом Psy-Trainer в личном кабинете.',
    robots: NOINDEX_META,
  },
  '/admin': {
    title: `Администрирование | ${SITE_NAME}`,
    description: 'Административная панель Psy-Trainer для управления тестами и материалами.',
    keywords: `${DEFAULT_KEYWORDS}, администрирование тестов`,
    ogDescription: 'Закрытая административная панель Psy-Trainer.',
    robots: NOINDEX_META,
  },
  '/admin/tests': {
    title: `Управление тестами | ${SITE_NAME}`,
    description: 'Закрытая административная страница Psy-Trainer для управления тестами.',
    keywords: `${DEFAULT_KEYWORDS}, управление тестами`,
    ogDescription: 'Закрытый раздел управления тестами Psy-Trainer.',
    robots: NOINDEX_META,
  },
  '/tests/new': {
    title: `Новый тест | ${SITE_NAME}`,
    description: 'Страница создания нового теста Psy-Trainer для администраторов.',
    keywords: `${DEFAULT_KEYWORDS}, создание теста`,
    ogDescription: 'Создание нового теста в административной панели Psy-Trainer.',
    robots: NOINDEX_META,
  },
  '/tests/:id/edit': {
    title: `Редактирование теста | ${SITE_NAME}`,
    description: 'Общая страница редактирования теста Psy-Trainer без раскрытия содержимого или персональных данных.',
    keywords: `${DEFAULT_KEYWORDS}, редактирование теста`,
    ogDescription: 'Редактирование теста в административной панели Psy-Trainer.',
    robots: NOINDEX_META,
  },
};

function ensureMeta(attribute, value) {
  let tag = document.head.querySelector(`meta[${attribute}="${value}"]`);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, value);
    document.head.appendChild(tag);
  }

  return tag;
}

function setMeta(attribute, value, content) {
  ensureMeta(attribute, value).setAttribute('content', content);
}

function isDynamicMatch(pattern, pathname) {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = pathname.split('/').filter(Boolean);

  if (patternParts.length !== pathParts.length) {
    return false;
  }

  return patternParts.every((part, index) => part.startsWith(':') || part === pathParts[index]);
}

export function getSeoMetaForPath(pathname) {
  if (ROUTE_META[pathname]) {
    return ROUTE_META[pathname];
  }

  const dynamicPattern = Object.keys(ROUTE_META).find((pattern) => pattern.includes(':') && isDynamicMatch(pattern, pathname));
  return ROUTE_META[dynamicPattern] || ROUTE_META['/'];
}

function getRouteUrl(pathname) {
  const origin = window.location.origin || '';
  return `${origin}${pathname}`;
}

function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = getSeoMetaForPath(pathname);
    const routeUrl = getRouteUrl(pathname);

    document.title = meta.title;
    setMeta('name', 'description', meta.description);
    setMeta('name', 'keywords', meta.keywords);
    setMeta('name', 'robots', meta.robots || INDEX_META);
    setMeta('property', 'og:title', meta.title);
    setMeta('property', 'og:description', meta.ogDescription || meta.description);
    setMeta('property', 'og:url', routeUrl);
  }, [pathname]);

  return null;
}

export default Seo;
